import React from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    MenuItem,
    Pagination,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import AndroidIcon from '@mui/icons-material/Android';
import { getUserEventsPaginated } from '../Api';
import AccountEventDto from '../models/dtos/AccountEventDto';

interface UserEventsTableProps {
    userId: number;
}

const PAGE_SIZE = 100;

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

const getCountryFlag = (code: string): string =>
    code.toUpperCase().replace(/./g, c => String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0)));

const getCountryName = (code: string): string => {
    try { return countryNames.of(code.toUpperCase()) ?? code; } catch { return code; }
};

export default function UserEventsTable({ userId }: UserEventsTableProps) {
    const [items, setItems] = React.useState<AccountEventDto[]>([]);
    const [loaded, setLoaded] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(1);

    const fetchData = async (p: number) => {
        setLoaded(false);
        const offset = (p - 1) * PAGE_SIZE;
        const response = await getUserEventsPaginated(userId, offset, PAGE_SIZE);
        if (response) {
            setItems(response.items ?? []);
            setTotalPages(Math.max(1, Math.ceil(response.totalItems / PAGE_SIZE)));
        }
        setPage(p);
        setLoaded(true);
    };

    React.useEffect(() => {
        fetchData(1);
    }, []);

    const handlePageChange = (_: React.ChangeEvent<unknown>, p: number) => fetchData(p);
    const handlePageSelectChange = (e: SelectChangeEvent<number>) => fetchData(Number(e.target.value));

    const osIcon = (osType: string | null) => {
        if (!osType) return null;
        if (osType.toLowerCase().includes('android')) {
            return <AndroidIcon sx={{ fontSize: 16, color: '#3ddc84', verticalAlign: 'middle', mr: 0.4 }} />;
        }
        return <img src='/ios_logo.png' width={16} height={16} alt='iOS' style={{ verticalAlign: 'middle', marginRight: 4 }} />;
    };

    if (!loaded) {
        return <CircularProgress size='64px' style={{ marginBottom: '32px' }} />;
    }

    return (
        <Box>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 180 }}>Date &amp; Time</TableCell>
                        <TableCell sx={{ width: 220 }}>Event Type</TableCell>
                        <TableCell>Params</TableCell>
                        <TableCell sx={{ width: 150 }}>Country</TableCell>
                        <TableCell sx={{ width: 120 }}>OS / Version</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary">No events found.</Typography>
                            </TableCell>
                        </TableRow>
                    ) : items.map((event, idx) => (
                        <TableRow key={idx} hover>
                            <TableCell>
                                <Typography variant="body2" noWrap>
                                    {new Date(event.createdAt).toLocaleString()}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" fontWeight="bold" noWrap>
                                    {event.eventType}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                {event.params && Object.keys(event.params).length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {Object.entries(event.params).map(([k, v]) => (
                                            <Tooltip key={k} title={`${k}: ${v}`} arrow>
                                                <Chip
                                                    label={<><Typography component="span" variant="caption" color="text.secondary">{k}:&nbsp;</Typography><Typography component="span" variant="caption">{v}</Typography></>}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ maxWidth: 220, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.3 } }}
                                                />
                                            </Tooltip>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                {event.countryCode ? (
                                    <Typography variant="body2" noWrap>
                                        {getCountryFlag(event.countryCode)}&nbsp;{getCountryName(event.countryCode)}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {osIcon(event.osType)}
                                    <Typography variant="body2" noWrap>{event.appVersion ?? '—'}</Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 2, alignItems: 'center', justifyContent: 'center' }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
                <Select
                    value={page}
                    onChange={handlePageSelectChange}
                    style={{ width: '150px' }}
                >
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <MenuItem key={p} value={p}>Page {p}</MenuItem>
                    ))}
                </Select>
            </Stack>
        </Box>
    );
}
